import { Component, ViewChild, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { CommonFunctions } from '../static/common-functions';
import { BaseBus } from '../models/BaseBus';
import { GlobalsService } from '../services/globals.service';
import { UntypedFormGroup } from '@angular/forms';
import { BasePage } from '../models/BasePage';
import { DQOIReferenceData } from '../models/DQOIReferenceData';
import { BaseDQOIComponent } from './base-dqoi.component';
import { GItem } from '../models/GItem';
import { GeographyInfoBus } from '../models/GeographyInfoBus';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { CRUDActions } from '../enums/CRUDActions';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { Subscription } from 'rxjs';
import { MessageType } from '../enums/MessageType';
import { Message } from '../models/message';
import { SaveMode } from '../enums/SaveMode';
import { ConfirmOutput } from '../models/ConfirmOutput';
import { CPDialogResult } from '../enums/CPDialogResult';
import { CommonParam } from '../models/CommonParam';
import { DateFunctions } from '../static/date-functions';
import { BusinessObjectFactory } from '../models/BusinessObjectFactory';
import { AppClaims } from '../models/AppClaims';
import { validateOverridables, EmptyOverridable } from '../decorators/overridable/base-method-override-decorator';
import { CommonValues } from '../static/CommonValues';

@Component({
  selector: '',
  template: ''
})
export class BaseEditComponent<T extends BaseBus, S, A extends BaseBus, P extends BasePage<S>>   implements OnDestroy {

    constructor(public globals: GlobalsService, qry_rd: DQOIReferenceDataQuery) {
        this.qry_rd = qry_rd;

        this.subs_dataRef = this.qry_rd.ensureRefDataIsLoaded(this.globals.companyId).subscribe();
        validateOverridables(this, Object.getPrototypeOf(this).constructor.name, BaseEditComponent);
    }

    @EmptyOverridable()
    setForm() {}

    @EmptyOverridable()
    setNewDefaults() { }

    @EmptyOverridable()
    afterDataSet() { }

    @EmptyOverridable()
    saveAll() { }

    @EmptyOverridable()
    saveIndividual() { }

    @EmptyOverridable()
    confirmCustom(data: ConfirmOutput) {}

    subs_data: Subscription;
    @ViewChild('cnfDialog') protected cnfDialog: ConfirmDialogComponent;
    @Output() closed: EventEmitter<any> = new EventEmitter<any>();
    @Input() setClosedOnSave: boolean = false;
    virtualizationSettings = CommonValues.virtualizationSettings;

    private _rights: AppClaims | null = null;

    /**
     * Using this getter can cause race conditions in code depending on how this is used.
     * While we have a user data  resolver in place to help, this still could cause issues.
     * If you need reliable data from the user object, use CmProAuthService.subscribeToLoginEvents and CmProAuthService.unsubscribeToLoginEvents in your component
     *
     * @readonly
     * @type {AppClaims}
     * @memberof BaseEditComponent
     */
    get rights(): AppClaims {
        return this._rights ?? this.globals.user?.appClaims ?? new AppClaims();
    }

    set rights(value: AppClaims) {
        this._rights = value;
    }
    appCode: string = null;
    className: string = "";
    svc_data: S;
    dataLoading: boolean = false;
    data: T;
    originalData: T;
    addtlData: A;
    pageData: P;
    qry_rd: DQOIReferenceDataQuery;
    subs_dataRef: Subscription;
    get referenceData(): DQOIReferenceData {
        return BaseDQOIComponent._referenceData;
    }
    id: any;
    public editForm: UntypedFormGroup;
    readOnly: boolean = true;
    customerLocations: GItem[] = null;
    customerGeoLocations: GItem[] = null;
    settingFromDQOIState: boolean = false;
    billToGeoInfo: GeographyInfoBus = new GeographyInfoBus();
    locationGeoInfo: GeographyInfoBus = new GeographyInfoBus();
    shippingGeoInfo: GeographyInfoBus = new GeographyInfoBus();
    customer: string;
    location: string;
    saveMode: SaveMode = SaveMode.all;
    saveLoadingButton: boolean = false;

    ngOnDestroy() {
        CommonFunctions.unsubscribe(this.subs_dataRef);
    }

    get hasReferenceData(): boolean {
        let retVal: boolean = false;
        try {
            retVal = this.referenceData !== undefined && this.referenceData !== null && this.referenceData.dispatchStatus !== undefined && this.referenceData.dispatchStatus !== null ? true : false;
        }
        catch{
            retVal = false;
        }
        return retVal;

    }
    public filterSettings: DropDownFilterSettings = {
        caseSensitive: false,
        operator: 'contains'
    };
    setDirty() {
        if (this.hasEditForm) {
            this.editForm.markAsDirty();
        }
    }
    setPristine() {
        if (this.hasEditForm) {
            this.editForm.markAsPristine();
        }
    }
    get hasEditForm(): boolean {
        return this.editForm !== undefined && this.editForm !== null ? true : false;
    }
    get hasData(): boolean {
        return this.data !== undefined && this.data !== null;
    }
    get canEdit(): boolean {
        return this.readOnly && this.hasData;
    }
    get canClear(): boolean {
        return this.readOnly && this.hasData;
    }
    get isDirty(): boolean {
        return !this.readOnly && this.hasEditForm && this.editForm.dirty;
    }

    private _canSaveValue: boolean = false;
    private updateCanSaveValue(): void {
        let okToSave: boolean = false;
        if (this.isDirty) {
            if (this.hasId) {
                okToSave = this.editForm.valid && this.customFormValidation();
            }
            else {
                okToSave = this.editForm.valid && this.customFormValidation();
            }
        }
        this._canSaveValue = okToSave || this._hasSaveError;
    }

    get canSave(): boolean {
        this.updateCanSaveValue();
        return this._canSaveValue;
    }

    /**
     * This is a property that is used to determine if the save operation has encountered an error.
     * This will control if the setPristine() method is called after the save operation.
     *
     * @protected
     * @type {boolean}
     * @memberof BaseAppMComponent
     */
    protected set hasSaveError(value: boolean) {
        if (this._hasSaveError !== value) {
            this._hasSaveError = value;
            this.updateCanSaveValue();
        }
    }
    private _hasSaveError: boolean = false;


    parentMessage(e: Message) {
        if (e.messageType === MessageType.saveParent) {
            this.save();
        }
        else if (e.messageType === MessageType.changed) {
            this.setDirty();
        }
    }

    get customerId(): any {
        return this.hasEditForm ? this.editForm.value.customerId : null;
    }
    get locationId(): any {
        return this.hasEditForm ? this.editForm.value.locationId : null;
    }
    get hasCustomer(): boolean {
        return CommonFunctions.hasValue(this.customerId);
    }
    get hasLocation(): boolean {
        return CommonFunctions.hasValue(this.locationId);
    }
    get hasId(): boolean {
        return this.hasData && this.data.hasId ? true : false;
    }
    customFormValidation() {
        return this.hasCustomer && this.hasLocation ? true : false;
    }
    customerCBOChanged(item: any) {
        if (CommonFunctions.hasValue(item)) {
            this.customerChanged(item.value);
        }
        else {
            this.customerChanged(null);
        }
    }
    customerChanged(id: any) {
        this.location = null;
        let custId: any = id;
        if (CommonFunctions.hasValue(this.referenceData) && CommonFunctions.hasArrayValue(this.referenceData.locations) && CommonFunctions.hasValue(id)) {
            this.customer = this.referenceData.customers.find(s => s.value === id).description;
            this.customerLocations = this.referenceData.locations.filter(e => e.category === id);
            this.customerGeoLocations = this.referenceData.locations.filter(e => e.reference === id);
        }
        else {
            this.customer = null;
            //  this.editForm.value.customerId = null;
            this.customerLocations = [];
            this.customerGeoLocations = [];
        }
        this.editForm.patchValue({
            customerId: custId
        });

        this.locationChanged(null);
    }
    locationCBOChanged(item: GItem) {
        if (CommonFunctions.hasValue(item)) {
            this.locationChanged(item.value);
        }
        else {
            this.locationChanged(null);
        }
    }
    locationChanged(id: any) {
        if (CommonFunctions.hasValue(id)) {
            this.location = this.referenceData.locations.find(s => s.value == id).description;
            this.getLocationDefaults(id);
        }
        else {
            this.editForm.patchValue({
                locationId: null
            });
            this.location = null;
        }
    }

    new() {
        this.clear();
        this.setNewDefaults();
        this.setData(this.data);
        this.edit();
    }
    getLocationDefaults(id: any) {

    }

    edit() {
        this.readOnly = false;
    }
    protected setNewObjDefaults(busObj: T) {
        busObj.setNew(this.globals.userId, DateFunctions.getDateNow(), this.globals.companyId, this.globals.personId);
        //userId: any, date: Date, companyId: any = null, personId: any = null, parentId: any = null, other: any = null
    }
    createNew(busObj: T): T {
//        let obj = {} as T;
        let obj: T = BusinessObjectFactory.Create(this.className, busObj);
        if (obj.initialize) {
            obj.initialize(busObj);
            this.setNewObjDefaults(obj);
        }
        return obj;
    }
    setData(data: T) {
        this.data = this.createNew(data);
        this.id = this.data.CID();
        this.originalData = _.cloneDeep(data);
        this.afterDataSet();
        this.setForm();
       
    }
    getDataForCancel() {
        return this.createNew(this.originalData);
    }
    cancel() {
        this.data = this.getDataForCancel();
        if (!this.data.hasId) {
            this.customerLocations = [];
            this.customerGeoLocations = [];
        }
        this.setForm();
        this.readOnly = true;
        this.closed.emit(false);

    }

    clear() {
        this.data = this.createNew(null);
        this.originalData = this.createNew(null);
        this.id = null;
        this.customerLocations = [];
        this.customerGeoLocations = [];
        this.readOnly = true;
        this.editForm.reset();
        if (CommonFunctions.hasValue(this.pageData)) {
            this.pageData.clear();
        }
        if (CommonFunctions.hasValue(this.addtlData)) {
            this.addtlData.clear();
        }
       
    }
    close(data: any = null) {
        this.clear();
        this.closed.emit(data);
    }
    save() {
        this.saveLoadingButton = true;
        if (this.saveMode === SaveMode.all) {
            this.saveAll();
        }
        else {
            this.saveIndividual();
        }
    }

    afterSave(flag: boolean) {
        if (flag && this.setClosedOnSave) {
            this.close(true);
        }
        this.saveLoadingButton = false;
    }
    saveSuccess(message: string = null) {
        let not: string = this.appCode + " Successfully Saved" + (CommonFunctions.hasStringValue(message) ? " " + message : "");
        this.globals.notifySuccess(not);
        this.afterSave(true);
    }
    saveError(message: string = null) {
        let not: string = "There was a problem saving " + this.appCode + (CommonFunctions.hasStringValue(message) ? " " + message : "");
        this.globals.notifyError(not, true);
        this.afterSave(false);
    }
    cnfShow(message: string, action: CRUDActions = CRUDActions.None, data: any = null, title: string = null, code: string = null) {
        if (CommonFunctions.hasValue(this.cnfDialog)) {
            this.cnfDialog.show(message, action, data, title, code);
        }
    }
    closeCtrlOpenFlag: boolean = false;
    ctrlOpenFlag: boolean = false;
    confirmDialog(data: ConfirmOutput) {
        this.closeCtrlOpenFlag = true;
        let handled: boolean = false;
        if (data.dialogResult === CPDialogResult.ok) {
            switch (data.action) {
                case CRUDActions.Delete:
                    handled = true;
                    break;
            }
        }
        if (!handled) {
            this.confirmCustom(data);
        }
    }
    seParam: CommonParam = new CommonParam();
    addParamValue(key: string, value: any) {
        this.seParam.addParam(key, value);
    }
}

